import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { bemGenerator } from '@prisjakt/ui-base';
import ArrowIcon from 'mdi-react/ChevronRightIcon';
import { AppContext } from '@prisjakt/ui-app-context';
import AdContainer from '../ads';
import { pushEvent } from '../../client/data-layer';

import './index.scss';

const styles = bemGenerator('category-list', [
  'image',

  'top-level-item',
  'top-level-link',
  'top-level-name',
  'top-level-icon',

  'sub-level',
  'sub-level-item',
  'sub-level-link',

  'mobile-inline-ad-1',
]);

const atLeast = count => item =>
  item && Array.isArray(item) && item.length >= count;
const atLeastOne = atLeast(1);

const haveChildren = item => atLeastOne(item.children);

const MAIN_LINK = 'main';
const SUBCATEGORY_LINK = 'subcategory';
const SHOW_ALL_LINK = 'showAll';

const trackClick = (category, linkKind) => {
  pushEvent('categoryListClick', {
    category,
    linkKind,
    isSubcategory: linkKind === SUBCATEGORY_LINK,
    insideHoverMenu: linkKind !== MAIN_LINK,
  });
};

const ads = {
  mobileInlineAd1: (
    <li
      className={`${styles['top-level-item']} ${styles['mobile-inline-ad-1']}`}
    >
      <AdContainer
        placement="dubbelpanorama1"
        min={0}
        max={860}
        spacing={{ bottom: '20px' }}
      />
    </li>
  ),
};

const CategoryList = ({ categories, limitSubCategories, withAds }) => {
  const { t, linkTo } = useContext(AppContext);
  const fewThreshold = 4;
  // Use pathName if set in order to handle campaign items
  const linkToItem = item => item.pathName || linkTo('category', item);

  return atLeastOne(categories) ? (
    <ul
      className={styles.block.withModifier({
        few: categories.length < fewThreshold,
      })}
      data-test="CategoryList"
    >
      {withAds && ads.mobileInlineAd1}

      {categories.map((topLevel, index) => (
        <li
          className={styles['top-level-item'].withModifier({
            'has-children': haveChildren(topLevel),
          })}
          key={topLevel.id}
          style={{ order: index }}
          data-test="CategoryListItem"
          data-test-level="top"
        >
          {topLevel.externalUri && (
            <a href={topLevel.externalUri} className={styles['top-level-link']}>
              <img
                className={styles.image}
                src={topLevel.logo[192] || topLevel.logo}
                alt=""
              />
              <span className={styles['top-level-name']}>{topLevel.name}</span>
              <span className={styles['top-level-icon']}>
                <ArrowIcon size={24} />
              </span>
            </a>
          )}
          {!topLevel.externalUri && (
            <Link
              className={styles['top-level-link']}
              to={linkToItem(topLevel)}
              onClick={() => trackClick(topLevel, MAIN_LINK)}
            >
              <img
                className={styles.image}
                src={topLevel.logo[192] || topLevel.logo}
                alt=""
              />
              <span className={styles['top-level-name']}>{topLevel.name}</span>
              <span className={styles['top-level-icon']}>
                <ArrowIcon size={24} />
              </span>
            </Link>
          )}

          {haveChildren(topLevel) && (
            <ul className={styles['sub-level']}>
              {topLevel.children.slice(0, limitSubCategories).map(subLevel => (
                <li key={subLevel.id} className={styles['sub-level-item']}>
                  {subLevel.externalUri && (
                    <a
                      className={styles['sub-level-link']}
                      href={subLevel.externalUri}
                    >
                      {subLevel.name}
                    </a>
                  )}
                  {!subLevel.externalUri && (
                    <Link
                      className={styles['sub-level-link']}
                      to={linkToItem(subLevel)}
                      onClick={() => trackClick(subLevel, SUBCATEGORY_LINK)}
                    >
                      {subLevel.name}
                    </Link>
                  )}
                </li>
              ))}
              <li
                className={styles['sub-level-item'].withModifier({
                  'show-all': true,
                })}
              >
                {topLevel.externalUri && (
                  <a
                    className={styles['sub-level-link']}
                    href={topLevel.externalUri}
                  >
                    {t('category', 'subcategories.show-all')}
                  </a>
                )}

                {!topLevel.externalUri && (
                  <Link
                    className={styles['sub-level-link']}
                    to={linkToItem(topLevel)}
                    onClick={() => trackClick(topLevel, SHOW_ALL_LINK)}
                  >
                    {t('category', 'subcategories.show-all')}
                  </Link>
                )}
              </li>
            </ul>
          )}
        </li>
      ))}
    </ul>
  ) : null;
};

const { arrayOf, number, shape, string, bool, oneOfType } = PropTypes;

const categoryShape = {
  id: string,
  name: string,
  semantic_id: string,
  pathName: string,
  logo: oneOfType([
    string,
    shape({
      192: string,
    }),
  ]),
};

const categoryWithChildrenShape = {
  ...categoryShape,
  children: arrayOf(shape(categoryShape)),
};

CategoryList.propTypes = {
  categories: arrayOf(shape(categoryWithChildrenShape)).isRequired,
  limitSubCategories: number,
  withAds: bool,
};

CategoryList.defaultProps = {
  limitSubCategories: 5,
  withAds: false,
};

export { CategoryList };
